import { useState, useEffect } from 'react';
import { useToast } from './use-toast';

export const useApi = (apiFunction, dependencies = [], options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { toast } = useToast();
  
  const { 
    showErrorToast = true, 
    showSuccessToast = false,
    retryAttempts = 0 
  } = options;

  const fetchData = async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await apiFunction();
      setData(result);
      
      if (showSuccessToast) {
        toast({
          title: "Success",
          description: "Data loaded successfully",
          variant: "default",
        });
      }
    } catch (err) {
      console.error('API Error:', err);
      setError(err);
      
      if (retryCount < retryAttempts) {
        setTimeout(() => fetchData(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
      
      if (showErrorToast) {
        toast({
          title: "Error loading data",
          description: err.response?.data?.detail || err.message || "An unexpected error occurred",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, dependencies);

  const refetch = () => fetchData();

  return { data, loading, error, refetch };
};

export const useAsyncApi = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const execute = async (apiFunction, options = {}) => {
    const { 
      showErrorToast = true, 
      showSuccessToast = true,
      successMessage = "Operation completed successfully"
    } = options;

    try {
      setLoading(true);
      const result = await apiFunction();
      
      if (showSuccessToast) {
        toast({
          title: "Success",
          description: successMessage,
          variant: "default",
        });
      }
      
      return result;
    } catch (err) {
      console.error('API Error:', err);
      
      if (showErrorToast) {
        toast({
          title: "Operation failed",
          description: err.response?.data?.detail || err.message || "An unexpected error occurred",
          variant: "destructive",
        });
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { execute, loading };
};